/*
  <localization-dropdown> — the language / country popover, ported from Concept's
  DropdownElement + DropdownLocalization.

  Click always toggles it, so it works on touch, where there is no hover. On
  hover-capable pointers it *also* opens on hover. Closing on mouseleave is
  delayed and cancelled if the pointer lands on the panel, so crossing the gap
  between trigger and panel doesn't slam it shut before you can pick an option.
  Closes on outside click, Escape, or when a sibling dropdown opens.

  The opacity/visibility fade lives in CSS — `[open]` is the only thing this
  toggles, plus `aria-expanded` on its triggers. A trigger is any element with
  aria-controls="<this element's id>".
*/
(function () {
  if (customElements.get('localization-dropdown')) return;

  /* Live, not sampled once at load: a hybrid device can gain or lose a mouse. */
  var hoverQuery = window.matchMedia('(hover: hover) and (pointer: fine)');

  /* Long enough to cross the gap between trigger and panel without hurrying. */
  var CLOSE_DELAY = 260;

  customElements.define(
    'localization-dropdown',
    class LocalizationDropdown extends HTMLElement {
      get controls() {
        return Array.from(document.querySelectorAll('[aria-controls="' + this.id + '"]'));
      }

      get open() {
        return this.hasAttribute('open');
      }

      connectedCallback() {
        if (this.bound) return;
        this.bound = true;

        var self = this;
        this.onOutside = this.detectClickOutside.bind(this);
        this.onEsc = this.detectEscape.bind(this);
        var enter = this.onPointerEnter.bind(this);
        var leave = this.onPointerLeave.bind(this);

        this.controls.forEach(function (control) {
          control.addEventListener('click', self.onToggleClick.bind(self));
          control.addEventListener('mouseenter', enter);
          control.addEventListener('mouseleave', leave);
        });

        // Also on the panel itself: leaving the trigger's subtree into the gap
        // fires mouseleave, and only a matching enter here can cancel the close.
        this.addEventListener('mouseenter', enter);
        this.addEventListener('mouseleave', leave);
      }

      setExpanded(value) {
        this.controls.forEach(function (control) {
          control.setAttribute('aria-expanded', value ? 'true' : 'false');
        });
      }

      closeSiblings() {
        var self = this;
        document.querySelectorAll('localization-dropdown[open]').forEach(function (other) {
          if (other !== self) other.hide();
        });
      }

      show() {
        clearTimeout(this.closeTimer);
        if (this.open) return;
        this.closeSiblings();
        this.setAttribute('open', '');
        this.setExpanded(true);
        document.addEventListener('click', this.onOutside);
        document.addEventListener('keyup', this.onEsc);
      }

      hide() {
        clearTimeout(this.closeTimer);
        if (!this.open) return;
        this.removeAttribute('open');
        this.setExpanded(false);
        document.removeEventListener('click', this.onOutside);
        document.removeEventListener('keyup', this.onEsc);
      }

      onToggleClick(event) {
        // Clicks on an option inside the panel must reach the form and submit.
        if (this.contains(event.target)) return;
        event.preventDefault();
        this.open ? this.hide() : this.show();
      }

      onPointerEnter() {
        if (!hoverQuery.matches) return;
        this.show();
      }

      onPointerLeave() {
        if (!hoverQuery.matches) return;
        clearTimeout(this.closeTimer);
        this.closeTimer = setTimeout(this.hide.bind(this), CLOSE_DELAY);
      }

      detectClickOutside(event) {
        if (this.contains(event.target)) return;
        if (this.parentElement && this.parentElement.contains(event.target)) return;
        this.hide();
      }

      detectEscape(event) {
        if (event.code === 'Escape') this.hide();
      }
    }
  );
})();
